<template>
  <NuxtLink
    v-if="isNuxtLink"
    :to="to"
    :target="target"
    :rel="computedRel"
    :aria-label="label"
    class="smart-nuxt-link"
  >
    <slot />
  </NuxtLink>
  <div
    v-else
    :aria-label="label"
    role="link"
    tabindex="0"
    class="smart-link"
    @click="handleClick"
    @keydown="handleKeydown"
  >
    <slot />
    <a
      :href="botHref"
      :target="target"
      :rel="computedRel"
      aria-hidden="true"
      tabindex="-1"
      class="bot-only"
      >{{ label }}</a
    >
  </div>
</template>

<script setup lang="ts">
import { withBase } from 'ufo';

/**
 * SmartLink component for handling both NuxtLink and anchor tags
 * N.B. This is a temporary workaround until we will find a solution for the issue with NuxtLink
 * in combination with GSAP animations.
 */
const props = defineProps({
  to: { type: String },
  label: { type: String, default: '' },
  tag: { type: String, default: 'NuxtLink' }, // 'NuxtLink' or 'a'
  target: { type: String, default: undefined },
  rel: { type: String, default: undefined },
});

const emit = defineEmits(['click']);

const router = useRouter();
const baseURL = useRuntimeConfig().app.baseURL || '/';

// Plain anchors aren't prefixed with the app baseURL (e.g. /preview/) like NuxtLink is.
const botHref = computed(() => (props.to ? withBase(props.to, baseURL) : props.to));

const isNuxtLink = computed(() => props.tag === 'NuxtLink');

const computedRel = computed(() =>
  props.target === '_blank' ? props.rel || 'noopener noreferrer' : undefined,
);

const handleClick = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();
  emit('click', e);
  if (!isNuxtLink.value) {
    router.push({ path: props.to });
  }
};

const handleKeydown = (e: KeyboardEvent) => {
  if (!isNuxtLink.value && (e.key === 'Enter' || e.key === ' ')) {
    e.preventDefault();
    handleClick(e as Event);
  }
};
</script>

<style lang="postcss" scoped>
.smart-link {
  position: relative;
}

.bot-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  z-index: -1;
}
</style>
